import type { Team, TeamArea, TeamMember } from '../api/types.js';
import { useTeam, usePeople } from '../hooks/use-people.js';

// The team view of a server (ADR-030 section 5): per team, its people and what they did each month over the last six,
// then the repositories they work in, with the thin ones, where at most one person of the team who stays works. A
// person in several teams shows in each. From the facts only.

const month = (m: string, style: 'short' | 'long' = 'short') => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: style, ...(style === 'long' ? { year: 'numeric' } : {}) });
const day = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

/** One square per month, darker as more was done; the busiest month of the server is the darkest */
function MonthStrip({ months, perMonth, max, label }: { months: string[]; perMonth: number[]; max: number; label: string }) {
  return (
    <span role="img" aria-label={`${label}: ${months.map((m, i) => `${month(m, 'long')} ${perMonth[i] ?? 0}`).join(', ')}`} className="grid grid-cols-6 gap-1">
      {months.map((m, i) => {
        const n = perMonth[i] ?? 0;
        return (
          <span key={m} title={`${month(m, 'long')}: ${n}`} className="h-4 rounded-sm"
            style={n ? { backgroundColor: 'var(--color-brand)', opacity: 0.3 + 0.7 * Math.sqrt(n / max) } : { backgroundColor: 'var(--color-surface-2)' }} />
        );
      })}
    </span>
  );
}

function Member({ m }: { m: TeamMember }) {
  return (
    <span className="whitespace-nowrap">
      <span style={{ color: m.leaving ? 'var(--color-text-3)' : 'var(--color-text-2)' }}>{m.name}</span>
      <span style={{ color: 'var(--color-text-3)' }}> {m.total}</span>
      {m.alone !== undefined && m.merged > 0 && <span style={{ color: 'var(--color-text-3)' }}>{`, ${m.alone} of ${m.merged} merged alone`}</span>}
      {!!m.reviews && <span style={{ color: 'var(--color-text-3)' }}>{`, ${m.reviews} reviewed`}</span>}
      {m.newSince && <span style={{ color: 'var(--color-brand-light)' }}>{`, new since ${month(m.newSince)}`}</span>}
      {m.leaving && m.leftOn && (
        <span style={{ color: 'var(--color-warn)' }}>{`, ${m.leftOn <= new Date().toISOString().slice(0, 10) ? 'left' : 'leaves'} ${day(m.leftOn)}`}</span>
      )}
    </span>
  );
}

/** Why an area is thin, in a few words */
function thinNote(area: TeamArea): string {
  const staying = area.people.filter((p) => !p.leaving);
  return staying.length ? `Only ${staying[0]!.name} works here` : 'No one who stays works here';
}

function Area({ area, months, max }: { area: TeamArea; months: string[]; max: number }) {
  return (
    <li className="grid gap-x-4 gap-y-1.5 border-b border-l-2 py-3 pl-3 sm:grid-cols-[minmax(0,15rem)_11rem_minmax(0,1fr)] sm:items-baseline"
      style={{ borderBottomColor: 'var(--color-border)', borderLeftColor: area.thin ? 'var(--color-warn)' : 'transparent' }} data-testid="team-area">
      <span className="min-w-0">
        <span className="block text-sm break-all" style={{ color: 'var(--color-text)' }}>{area.repo}</span>
        {area.thin && <span className="text-xs" style={{ color: 'var(--color-warn)' }}>{thinNote(area)}</span>}
      </span>
      <span className="self-center"><MonthStrip months={months} perMonth={area.perMonth} max={max} label={area.repo} /></span>
      <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
        {area.people.map((m) => <Member key={m.personId} m={m} />)}
      </span>
    </li>
  );
}

/** A team: who is in it and what they did, then the repositories they work in */
function TeamSection({ team, months, max }: { team: Team; months: string[]; max: number }) {
  const name = team.name ?? 'No team';
  const thin = team.areas.filter((a) => a.thin).length;
  const active = team.members.filter((m) => m.total > 0);
  const idle = team.members.length - active.length;
  return (
    <section className="mb-8" aria-label={name} data-testid="team">
      <div className="grid gap-x-4 gap-y-1.5 pl-3 sm:grid-cols-[minmax(0,15rem)_11rem_minmax(0,1fr)] sm:items-baseline">
        <span className="min-w-0">
          <h3 className="text-sm font-semibold break-words" style={{ color: team.name ? 'var(--color-text)' : 'var(--color-text-2)' }}>{name}</h3>
          <span className="text-xs" style={{ color: thin ? 'var(--color-warn)' : 'var(--color-text-3)' }}>
            {team.areas.length ? `${thin ? `${thin} of ${team.areas.length} repositories thin` : `${team.areas.length} ${team.areas.length === 1 ? 'repository' : 'repositories'}, none thin`}` : 'Nothing in the last 6 months'}
          </span>
        </span>
        <span className="self-center"><MonthStrip months={months} perMonth={team.perMonth} max={max} label={name} /></span>
        <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
          {active.map((m) => <Member key={m.personId} m={m} />)}
          {idle > 0 && <span style={{ color: 'var(--color-text-3)' }}>{`${idle} with nothing in the view`}</span>}
        </span>
      </div>
      {team.areas.length > 0 && <ul className="mt-1">{team.areas.map((a) => <Area key={a.repo} area={a} months={months} max={max} />)}</ul>}
    </section>
  );
}

export function Team({ serverId }: { serverId: string }) {
  const { data, error, isLoading } = useTeam(serverId);
  const { data: people } = usePeople(serverId);
  if (isLoading) return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>Loading the team view…</p>;
  if (error) return <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Could not load it: {(error as Error).message}</p>;
  if (!data?.teams.length) {
    return <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Nothing seen in the last 6 months.{people?.github !== false ? ' Read GitHub from the people list to fill it.' : ''}</p>;
  }
  const { months, teams } = data;
  // One scale for every strip, teams and repositories: the busiest month of the server is the darkest
  const max = teams.reduce((m, t) => t.perMonth.reduce((n, v) => Math.max(n, v), t.areas.reduce((k, a) => a.perMonth.reduce((j, v) => Math.max(j, v), k), m)), 1);
  const areas = teams.flatMap((t) => t.areas);
  const named = teams.filter((t) => t.name).length;
  // Facts from before the per-month count have totals but no months, until GitHub is read again
  const undated = areas.some((a) => a.total > 0 && a.perMonth.every((n) => n === 0));
  return (
    <div>
      <p className="mb-4 max-w-[72ch] text-sm" style={{ color: 'var(--color-text-3)' }}>
        {`Each team, from ${month(months[0]!, 'long')} to ${month(months[months.length - 1]!, 'long')}: its people, then the repositories they work in, busiest first. A repository is thin when at most one person of the team who stays works there.`}
        {areas.some((a) => a.people.some((m) => m.alone !== undefined)) && ' Merged alone: no one asked for changes.'}
        {!named && ' No one has a team yet: set teams on each person in the people list.'}
      </p>
      {undated && people?.github !== false && (
        <p className="mb-4 text-xs" style={{ color: 'var(--color-text-3)' }}>Some activity was counted before months were kept: read GitHub again from the people list to see it month by month.</p>
      )}
      <div aria-hidden="true" className="mb-2 hidden gap-x-4 pl-3 text-xs sm:grid sm:grid-cols-[minmax(0,15rem)_11rem_minmax(0,1fr)]" style={{ color: 'var(--color-text-3)' }}>
        <span>Team, then its repositories</span>
        <span className="grid grid-cols-6 gap-1">{months.map((m) => <span key={m}>{month(m).slice(0, 3)}</span>)}</span>
        <span>People, with what they did there</span>
      </div>
      {teams.map((t) => <TeamSection key={t.name ? `team:${t.name}` : 'no-team'} team={t} months={months} max={max} />)}
    </div>
  );
}
